import * as _ from "lodash";

import { IMessageProvider, IProgressProvider, IIdentityProvider } from "./PA.Core.IF.Basic";
import { ESRecordResolver } from "./lib/ESRecordResolver";
import { BehaviorSubject } from "rxjs";
import { IRecordChange, IRecordChangesOptions } from "./lib/ESRecordChanges";
import { PAdbContext } from "./PAdbContext";

interface IBusinessObject {
    readonly Identity: IIdentityProvider;
    LoadNew(options?: any): Promise<IBusinessObject>;
    Load(anId: number | string, options?: any): Promise<IBusinessObject>;
    readonly bIsLoaded: boolean;
    readonly OnLoadComplete: Promise<IBusinessObject>;
    readonly LoadProgressProvider: IProgressProvider;

    Reload?: (opts?: any) => Promise<IBusinessObject>;
    readonly OnReloaded?: BehaviorSubject<IBusinessObject>;

    Validate(options?: any): Promise<IBusinessObject>;
    readonly bIsValidated: boolean;
    readonly ValidateMessageProvider: IMessageProvider;
    readonly ValidateProgressProvider: IProgressProvider;

    Save(options?: any): Promise<IBusinessObject>;
    SaveBatch(options?: any): Promise<IBusinessObject>;
    readonly bIsSaved: boolean;
    readonly SaveProgressProvider: IProgressProvider;

    Delete(options?: any): Promise<IBusinessObject>;
    DeleteOnSave?(options?: any): Promise<IBusinessObject>;
    readonly bIsDeleted: boolean;
    readonly DeleteMessageProvider: IMessageProvider;
    readonly DeleteProgressProvider: IProgressProvider;

    Copy?(options?: any): Promise<IBusinessObject>;

    Changes?(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges;

    // User Exits
    afterCreate?: () => void;
    beforeLoad?: () => Promise<void>;
    replaceLoad?: () => Promise<void>;
    afterLoad?: () => Promise<void>;
    afterLoadNew?: () => Promise<void>;
    beforeValidate?: () => Promise<void>;
    replaceValidate?: () => Promise<void>;
    afterValidate?: () => Promise<void>;
    beforeSave?: () => Promise<void>;
    replaceSave?: () => Promise<void>;
    afterSave?: () => Promise<void>;
    beforeDelete?: () => Promise<void>;
    replaceDelete?: () => Promise<void>;
    beforeClose?: () => boolean;
    afterDelete?: () => Promise<void>;
    afterCopy?: (originalBO: IBusinessObject, options?: any) => Promise<void>;
}

function implementsIBusinessObject(obj: any): boolean {
    if (_.isEmpty(obj)) {
        return false;
    }

    if (!_.isObject(obj)) {
        return false;
    }

    if (!("LoadNew" in obj)) {
        return false;
    }
    // @ts-ignore
    if (!_.isFunction(obj["LoadNew"])) {
        return false;
    }

    if (!("Load" in obj)) {
        return false;
    }
    // @ts-ignore
    if (!_.isFunction(obj["Load"])) {
        return false;
    }

    if (!("Validate" in obj)) {
        return false;
    }
    // @ts-ignore
    if (!_.isFunction(obj["Validate"])) {
        return false;
    }

    if (!("Save" in obj)) {
        return false;
    }
    // @ts-ignore
    if (!_.isFunction(obj["Save"])) {
        return false;
    }

    if (!("SaveBatch" in obj)) {
        return false;
    }
    // @ts-ignore
    if (!_.isFunction(obj["SaveBatch"])) {
        return false;
    }

    if (!("Delete" in obj)) {
        return false;
    }
    // @ts-ignore
    if (!_.isFunction(obj["Delete"])) {
        return false;
    }

    return true;
}

interface IBusinessObjectCreateLoadOptions {
    dbContext?: PAdbContext;

    // Create Options
    wbMasterDataSetId?: number;
    sId?: string;
    sKeySetId?: string;
    sName?: string;
    templateInd?: boolean
    wbCompanyId?: number;
    wbSalesOrgId?: number;
    wbSalesAreaId?: number | number[];
    wbSourceId?: number;
    sVolumeType?: string;
    wbModelEventTypeId?: number;
    wbModelProfileId?: number;
    wbModelId?: number;
    wbPublishedModelId?: number;
    wbEntityIdCustomer?: number;
    wbCustHId?: number;
    wbEntityIdProduct?: number;
    wbPrdHId?: number;
    wbEntityIdPeriod?: number;
    wbPeriodHId?: number;
    wbMetaTypeId?: number;
    sType?: string;

    settings?: IBusinessObjectCreateSettings;
}

interface IBusinessObjectCreateSettings {
    bShowMasterDataSet?: boolean
    bShowKeySet?: boolean
    keySetType?: string
    bShowId?: boolean
    bShowName?: boolean
    bShowTemplateInd?: boolean
    bShowSource?: boolean
    bShowCompany?: boolean
    bShowSalesOrg?: boolean
    bShowSalesArea?: boolean
    bShowModel?: boolean
    bShowModelEventType?: boolean
    bShowModelProfile?: boolean
    modelProfileType?: "" | "Simulator" | "Optimizer",
    bShowModelProfileVolumeType?: boolean,
    bShowPublishedModel?: boolean
    bShowCustomerEntity?: boolean
    bShowCustomerHierarchy?: boolean
    bShowProductEntity?: boolean
    bShowProductHierarchy?: boolean
    bShowPeriodEntity?: boolean
    bShowPeriodHierarchy?: boolean
    bShowMarket?:boolean
    metaType?: "" |  "ControlPointType" | "CustomerType" | "ProductType" | "PeriodType" | "PolicyArea"
    metaTypeId?: string // used to default control point type

    allOptionText?: {[id:string]: string}
    forceSelect?: {[id:string]: boolean}

    bRunIdCheck?: boolean

}

interface IBusinessObjectChangesOptions extends IRecordChangesOptions {
    [id: string]: any;
}

interface IBusinessObjectChanges {
    bHasChanges: boolean;
    bMainRecordExists: boolean;

    Changes: { Message: string; RecordChange: IRecordChange }[];
}

interface IRecordResolverProvider {
    // expecting the class to implement this.. if it has , the Bulk Loader will take advantage of it.
    createRecordResolver(boLoadOptions: any): ESRecordResolver;
    setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void>;
    concurrentLoadCountHint(): number;
}

function implementsIRecordResolverProvider(obj: any): boolean {
    if (_.isUndefined(obj)) {
        return false;
    }

    if (!_.isObject(obj)) {
        return false;
    }

    if (!("createRecordResolver" in obj)) {
        return false;
    }

    // @ts-ignore
    if (!_.isFunction(obj["createRecordResolver"])) {
        return false;
    }

    if (!("setupRecordResolver" in obj)) {
        return false;
    }

    // @ts-ignore
    if (!_.isFunction(obj["setupRecordResolver"])) {
        return false;
    }

    if (!("concurrentLoadCountHint" in obj)) {
        return false;
    }

    // @ts-ignore
    if (!_.isFunction(obj["concurrentLoadCountHint"])) {
        return false;
    }

    return true;
}

export {
    IBusinessObject,
    IRecordResolverProvider,
    implementsIRecordResolverProvider,
    IBusinessObjectChangesOptions,
    IBusinessObjectChanges,
    IBusinessObjectCreateLoadOptions,
};
